/**
 * Collapsible tool-activity block — "Ran 9 commands", modelled on Claude
 * Desktop's and sitting beside `ThinkingBlock`.
 *
 * The header tracks the run live; the body opens only on a click. That is the
 * one departure from `ThinkingBlock`, which auto-expands: reasoning is prose
 * that ends with the answer, while a tool log grows in place for the whole
 * run, so expanding it live would reflow the transcript once per call.
 *
 * No failure marker or per-row summary: openclaw's state frames carry a name
 * and nothing else (see `ToolCall`).
 */
import { useId, useState } from 'react';
import styled from 'styled-components';

import type { ToolCall } from '@paradigm/terminal-core';

import {
  DisclosureCaret,
  DisclosureHeader,
  DisclosureLabel,
  DisclosureWrap,
} from './disclosure-chrome';

/** Header summary. Trimmed calls are folded into the count; the body says so. */
export function summarizeToolCalls(count: number, live: string | null): string {
  if (live != null) return `Running ${live}`;
  if (count === 1) return 'Ran 1 command';
  return `Ran ${count} commands`;
}

export function ToolCallsBlock({
  toolCalls,
  dropped = 0,
  isToolActive,
}: {
  readonly toolCalls: readonly ToolCall[];
  readonly dropped?: number;
  readonly isToolActive: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();

  const total = dropped + toolCalls.length;
  const liveName = isToolActive
    ? (toolCalls[toolCalls.length - 1]?.name ?? null)
    : null;
  const label = summarizeToolCalls(total, liveName);

  // No live region, unlike `ThinkingBlock`: the in-flight row already
  // announces this call off the same frames, and a region here would both
  // double-announce and shadow it as the first `role="status"` in the DOM.
  return (
    <DisclosureWrap>
      <DisclosureHeader
        type="button"
        aria-expanded={expanded}
        aria-controls={expanded ? bodyId : undefined}
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <DisclosureCaret $open={expanded} aria-hidden="true">
          ▸
        </DisclosureCaret>
        <DisclosureLabel $live={isToolActive}>{label}</DisclosureLabel>
      </DisclosureHeader>
      {/* Focusable because it scrolls — see `ThinkingBlock`'s body. */}
      {expanded && (
        <Body id={bodyId} role="region" aria-label="Tool calls" tabIndex={0}>
          {dropped > 0 && (
            <Trimmed>{`… ${dropped} earlier ${dropped === 1 ? 'call' : 'calls'} trimmed`}</Trimmed>
          )}
          {/* Positional keys: append-only, and identical rows are legitimate. */}
          {toolCalls.map((call, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={`tool-${i}`}>
              <RowName>{call.name}</RowName>
            </Row>
          ))}
        </Body>
      )}
    </DisclosureWrap>
  );
}

const Body = styled.div`
  margin-top: 6px;
  padding-left: 10px;
  border-left: 2px solid var(--border-color);
  /* Bounded so a long log cannot push the answer off-screen. */
  max-height: 14em;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 2px 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const RowName = styled.span`
  font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 0.75rem;
  /* Gateway-controlled text must not blow out the chat column. */
  word-break: break-all;
`;

const Trimmed = styled.div`
  padding: 2px 0;
  font-size: 0.75rem;
  font-style: italic;
  color: var(--text-secondary);
  opacity: 0.7;
`;
